import { useCallback, useMemo, useState } from 'react';
import { buildWidgetConfirmationCopy } from '../../utils/case-plan-widget.util';

const EMPTY_COPY = { title: '', description: '', modalStatus: 'danger' };

/**
 * Confirmation state for the destructive widget actions (Delete on Draft, Archive on
 * Active/Released). The dialog itself is the shared confirmation `Modal`; this hook only
 * decides what it says and what happens on Confirm.
 *
 * @param {(sectionId: string, widget: object, action: 'delete' | 'archive') => Promise<void> | void} onConfirmed
 * @returns {{
 *   requestWidgetRemoval: (sectionId: string, widget: object, action: 'delete' | 'archive') => void,
 *   widgetConfirmation: {
 *     isOpen: boolean,
 *     title: string,
 *     description: string,
 *     modalStatus: 'danger' | 'info',
 *     onConfirm: () => void,
 *     onCancel: () => void,
 *   },
 * }}
 */
export const useCasePlanWidgetConfirmation = (onConfirmed) => {
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const requestWidgetRemoval = useCallback((sectionId, widget, action) => {
    setPendingRemoval({ sectionId, widget, action });
  }, []);

  const cancelWidgetRemoval = useCallback(() => {
    setPendingRemoval(null);
  }, []);

  const confirmWidgetRemoval = useCallback(() => {
    if (!pendingRemoval) {
      return;
    }

    const { sectionId, widget, action } = pendingRemoval;
    setPendingRemoval(null);
    onConfirmed(sectionId, widget, action);
  }, [onConfirmed, pendingRemoval]);

  const copy = useMemo(
    () =>
      pendingRemoval
        ? buildWidgetConfirmationCopy(pendingRemoval.widget, pendingRemoval.action)
        : null,
    [pendingRemoval],
  );

  return {
    requestWidgetRemoval,
    widgetConfirmation: {
      isOpen: copy !== null,
      ...(copy ?? EMPTY_COPY),
      onConfirm: confirmWidgetRemoval,
      onCancel: cancelWidgetRemoval,
    },
  };
};
